/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bookmark, EyeOff, Flag, MessageCircle, Send, Share2, VolumeX } from "lucide-react";
import { toast } from "sonner";

import FullScreenImage from "@/components/FullScreenImage";
import KindredShareSheet from "@/components/KindredShareSheet";
import KindredSharedContent from "@/components/KindredSharedContent";
import KinfeedMedia from "@/components/KinfeedMedia";
import MentionComposerField from "@/components/MentionComposerField";
import MessageActions from "@/components/MessageActions";
import ReactionBar from "@/components/ReactionBar";
import ReportSheet from "@/components/ReportSheet";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { hideFeedPost, muteFeedMember } from "@/lib/feedPreferences";
import { createKinfeedComment, toggleKinfeedCommentReaction, toggleKinfeedPostReaction } from "@/lib/kinfeedApi";
import { buildKindredWebUrl, composeKindredContent, parseKindredShareContent, type KindredSharePayload } from "@/lib/kindredShare";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import { dispatchMentionNotifications, searchAllMentionSuggestions } from "@/lib/mentions";
import { dispatchNotification } from "@/lib/pushNotifications";
import { fetchSavedFeedPostIds, toggleSavedFeedPost } from "@/lib/savedFeedPosts";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import {
  buildFeedCommentThreads,
  getKinfeedTopicTag,
  groupFeedPostReactions,
  groupScopedReactions,
  KINFEED_MAX_TOPIC_TAGS,
  KINFEED_POST_MAX_LENGTH,
  KINFEED_TOPIC_TAGS,
  isKinfeedPostWithinLimit,
  normalizeKinfeedTopicTags,
  type KinfeedTopicTagId,
  type MentionDraft,
} from "../../shared/community";
import {
  canEditOwnedContentOnly,
  canRemoveKinfeedOrChannelContent,
  isAdminModeratingOthersContent,
} from "../../shared/system";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppErrorState,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from "../../ui-foundation";

type ReplyTarget = {
  parentId: string;
  recipientId: string | null;
  name: string;
};

const FeedPostDetail = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [post, setPost] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentReactions, setCommentReactions] = useState<any[]>([]);
  const [postReactions, setPostReactions] = useState<any[]>([]);
  const [likes, setLikes] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [newCommentMentions, setNewCommentMentions] = useState<MentionDraft[]>([]);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const [sending, setSending] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [postEditText, setPostEditText] = useState("");
  const [postEditTopics, setPostEditTopics] = useState<KinfeedTopicTagId[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [fullScreenImages, setFullScreenImages] = useState<{ images: string[]; index: number } | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const [resolvedVideoUrls, setResolvedVideoUrls] = useState<Map<string, string>>(new Map());
  const [reportOpen, setReportOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const composerRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    if (!postId) return;
    const [{ data: nextPost }, { data: nextComments }, { data: nextLikes }, { data: nextPostReactions }] = await Promise.all([
      supabase
        .from("feed_posts" as any)
        .select("*, profiles(id, display_name, avatar_url, postcode, country_code)")
        .eq("id", postId)
        .single(),
      supabase
        .from("feed_comments" as any)
        .select("*, profiles(id, display_name, avatar_url)")
        .eq("post_id", postId)
        .eq("is_deleted", false)
        .order("created_at", { ascending: true }),
      supabase.from("feed_post_likes" as any).select("*").eq("post_id", postId),
      supabase.from("feed_post_reactions").select("*").eq("post_id", postId),
    ]);
    const commentIds = ((nextComments as any[]) ?? []).map((comment) => comment.id);
    const { data: nextCommentReactions } = commentIds.length
      ? await supabase.from("feed_comment_reactions" as any).select("*").in("comment_id", commentIds)
      : { data: [] as any[] };

    setPost(nextPost ?? null);
    if (nextPost && !editingPost) {
      const parsed = parseKindredShareContent((nextPost as any).content);
      setPostEditText(parsed.payload ? parsed.text : (nextPost as any).content ?? "");
      setPostEditTopics(normalizeKinfeedTopicTags((nextPost as any).topic_tags));
    }
    setComments((nextComments as any[]) ?? []);
    setCommentReactions((nextCommentReactions as any[]) ?? []);
    setPostReactions((nextPostReactions as any[]) ?? []);
    setLikes((nextLikes as any[]) ?? []);
    setLoading(false);
  }, [editingPost, postId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    let active = true;
    const photoValues = post?.photo_urls?.filter(Boolean) ?? [];
    const videoValues = post?.video_urls?.filter(Boolean) ?? [];
    void Promise.all([
      resolveSignedStorageUrls("community-photos", photoValues),
      resolveSignedStorageUrls("community-media", videoValues),
    ]).then(([photoMap, videoMap]) => {
      if (!active) return;
      setResolvedPhotoUrls(photoMap);
      setResolvedVideoUrls(videoMap);
    });
    return () => { active = false; };
  }, [post?.photo_urls, post?.video_urls]);

  useEffect(() => {
    if (!postId) return;
    const channel = supabase
      .channel(`feed-post-web-${postId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_comments", filter: `post_id=eq.${postId}` }, () => void fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_post_likes", filter: `post_id=eq.${postId}` }, (payload: any) => {
        if (payload.eventType === "INSERT") setLikes((current) => current.some((like) => like.id === payload.new.id) ? current : [...current, payload.new]);
        if (payload.eventType === "DELETE") setLikes((current) => current.filter((like) => like.id !== payload.old?.id));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_post_reactions", filter: `post_id=eq.${postId}` }, (payload: any) => {
        if (payload.eventType === "INSERT") setPostReactions((current) => current.some((reaction) => reaction.id === payload.new.id) ? current : [...current, payload.new]);
        if (payload.eventType === "DELETE") setPostReactions((current) => current.filter((reaction) => reaction.id !== payload.old?.id));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_comment_reactions" }, () => void fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchData, postId]);

  useEffect(() => {
    if (!postId || !user?.id) {
      setSaved(false);
      return;
    }
    void fetchSavedFeedPostIds(user.id)
      .then((ids) => setSaved(ids.includes(postId)))
      .catch((error) => console.error("Could not load saved post state:", error));
  }, [postId, user?.id]);

  const sendComment = async () => {
    const content = newComment.trim();
    if (!content || !user?.id || !postId || sending) return;
    setSending(true);
    const { error } = await createKinfeedComment(postId, user.id, content, replyTarget?.parentId ?? null);
    if (error) {
      setSending(false);
      toast.error("Couldn't add your comment");
      return;
    }

    const { data: profile } = await supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle();
    const actorName = profile?.display_name ?? "Someone";
    const recipients = new Map<string, string>();
    if (post?.user_id && post.user_id !== user.id) recipients.set(post.user_id, `${actorName} commented on your post`);
    if (replyTarget?.recipientId && replyTarget.recipientId !== user.id) {
      recipients.set(replyTarget.recipientId, `${actorName} replied to your comment`);
    }
    await Promise.all(Array.from(recipients).map(([recipientId, body]) => dispatchNotification({
      userId: recipientId,
      type: "post_reply",
      body,
      url: `/community/feed/${postId}`,
      entityType: "post",
      entityId: postId,
    })));
    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName: actorName,
      content,
      mentions: newCommentMentions,
      bodyContext: "in a Kinfeed comment",
      entityType: "post",
      entityId: postId,
      url: `/community/feed/${postId}`,
    });

    setNewComment("");
    setNewCommentMentions([]);
    setReplyTarget(null);
    setSending(false);
    await fetchData();
  };

  const togglePostReaction = async (emoji: string) => {
    if (!user?.id || !postId) return;
    const currentEmoji = likes.some((like) => like.user_id === user.id)
      ? "❤️"
      : postReactions.find((reaction) => reaction.user_id === user.id)?.emoji ?? null;
    const result = await toggleKinfeedPostReaction(postId, emoji);
    if (result.error) {
      toast.error("Couldn't update your reaction");
      return;
    }

    if (result.data === "❤️" && currentEmoji !== "❤️" && post?.user_id !== user.id) {
      const { data: profile } = await supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle();
      await dispatchNotification({
        userId: post.user_id,
        type: "post_like",
        body: `${profile?.display_name ?? "Someone"} liked your post`,
        url: `/community/feed/${postId}`,
        entityType: "post",
        entityId: postId,
      });
    }

    await fetchData();
  };

  const deleteComment = async (commentId: string, hiddenByAdmin: boolean) => {
    const { error } = await supabase.from("feed_comments" as any).update({ is_deleted: true } as any).eq("id", commentId);
    if (error) {
      toast.error("Couldn't remove this comment");
      return;
    }
    setComments((current) => current.filter((comment) => comment.id !== commentId && comment.parent_id !== commentId));
    toast.success(hiddenByAdmin ? "Comment removed" : "Comment deleted");
  };

  const deletePost = async (hiddenByAdmin: boolean) => {
    const { error } = await supabase.from("feed_posts" as any).update({ is_deleted: true } as any).eq("id", postId);
    if (error) {
      toast.error("Couldn't remove this post");
      return;
    }
    toast.success(hiddenByAdmin ? "Post removed from Kinfeed" : "Post deleted");
    navigate("/kinfeed");
  };

  const handleToggleSaved = async () => {
    if (!postId || !user?.id) return;
    setSaved((current) => !current);
    const result = await toggleSavedFeedPost(postId, user.id, saved);
    if (result.error) {
      setSaved(saved);
      toast.error("Couldn't update saved post");
      return;
    }
    toast.success(saved ? "Post removed from saved" : "Post saved");
  };

  const savePostEdit = async () => {
    if (!postEditText.trim() || !post) return;
    if (!isKinfeedPostWithinLimit(postEditText)) {
      toast.error(`Posts can contain up to ${KINFEED_POST_MAX_LENGTH.toLocaleString("en-GB")} characters`);
      return;
    }
    const parsed = parseKindredShareContent(post.content);
    const content = composeKindredContent(postEditText, parsed.payload);
    const { error } = await supabase.from("feed_posts" as any).update({
      content,
      edited_at: new Date().toISOString(),
      topic_tags: postEditTopics,
    } as any).eq("id", postId);
    if (error) {
      toast.error("Couldn't update this post");
      return;
    }
    setEditingPost(false);
    await fetchData();
    toast.success("Post updated");
  };

  const togglePostEditTopic = (tagId: KinfeedTopicTagId) => {
    setPostEditTopics((current) => {
      if (current.includes(tagId)) return current.filter((value) => value !== tagId);
      if (current.length >= KINFEED_MAX_TOPIC_TAGS) return [...current.slice(1), tagId];
      return [...current, tagId];
    });
  };

  const saveEditComment = async (commentId: string) => {
    if (!editText.trim()) return;
    const { error } = await supabase.from("feed_comments" as any).update({ content: editText.trim(), edited_at: new Date().toISOString() } as any).eq("id", commentId);
    if (error) {
      toast.error("Couldn't update this comment");
      return;
    }
    setEditingCommentId(null);
    await fetchData();
  };

  const toggleCommentReaction = async (commentId: string, emoji: string) => {
    if (!user?.id) return;
    const result = await toggleKinfeedCommentReaction(commentId, emoji);
    if (result.error) {
      toast.error("Couldn't update your reaction");
      return;
    }
    await fetchData();
  };

  const shareOutsideKindred = async () => {
    if (!postId || !post) return;
    const url = buildKindredWebUrl(`/community/feed/${postId}`);
    const parsed = parseKindredShareContent(post.content);
    const text = parsed.payload ? parsed.text || parsed.payload.title : `${post.profiles?.display_name ?? "Someone"} on Kinfeed`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Kinfeed post", text, url });
      } catch {
        // The member closed the native share sheet.
      }
      return;
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Post link copied");
  };

  const startReply = (comment: any, rootId: string) => {
    setReplyTarget({ parentId: rootId, recipientId: comment.user_id ?? null, name: comment.profiles?.display_name ?? "Member" });
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const diffHours = (Date.now() - date.getTime()) / 3600000;
    if (diffHours < 1) return `${Math.max(1, Math.floor(diffHours * 60))}m ago`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  if (loading) {
    return (
      <MemberPageScaffold title="Post" subtitle="Loading the post and comments." fallbackPath="/kinfeed">
        <AppLoadingState title="Loading post" body="Pulling in the conversation." />
      </MemberPageScaffold>
    );
  }

  if (!post) {
    return (
      <MemberPageScaffold title="Post" subtitle="This post is no longer available." fallbackPath="/kinfeed">
        <AppErrorState title="Post not found" body="It may have been removed or the link may be out of date." action={
          <AppButton type="button" tone="outline" onPress={() => navigate("/kinfeed")}>Back to Kinfeed</AppButton>
        } />
      </MemberPageScaffold>
    );
  }

  const postReactionSummary = groupFeedPostReactions(postId, likes, postReactions, user?.id);
  const photos = (post.photo_urls ?? [])
    .map((url: string) => resolveStoredObjectUrl("community-photos", url, resolvedPhotoUrls))
    .filter(Boolean) as string[];
  const videos = (post.video_urls ?? [])
    .map((url: string) => resolveStoredObjectUrl("community-media", url, resolvedVideoUrls))
    .filter(Boolean) as string[];
  const canEditPost = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: post.user_id });
  const canRemovePost = canRemoveKinfeedOrChannelContent({ viewerId: user?.id, ownerId: post.user_id, role });
  const isAdminModeratingPost = isAdminModeratingOthersContent({ viewerId: user?.id, ownerId: post.user_id, role });
  const authorName = post.profiles?.display_name ?? "Member";
  const locationLabel = formatPublicLocation(post.profiles?.postcode, post.profiles?.country_code);
  const postTopicTags = normalizeKinfeedTopicTags(post.topic_tags);
  const commentThreads = buildFeedCommentThreads(comments);
  const sharePayload: KindredSharePayload = {
    version: 1,
    type: "feed_post",
    targetId: post.id,
    title: parseKindredShareContent(post.content).text?.slice(0, 150) || `${authorName}'s Kinfeed post`,
    subtitle: `${authorName} on Kinfeed`,
    imageUrl: photos[0] ?? null,
    route: `/community/feed/${post.id}`,
    externalUrl: buildKindredWebUrl(`/community/feed/${post.id}`),
    accessScope: "members",
  };

  const renderComment = (comment: any, rootId: string, nested = false) => {
    const canEditComment = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: comment.user_id });
    const canRemoveComment = canRemoveKinfeedOrChannelContent({ viewerId: user?.id, ownerId: comment.user_id, role });
    const isAdminModeratingComment = isAdminModeratingOthersContent({ viewerId: user?.id, ownerId: comment.user_id, role });
    const reactions = groupScopedReactions(commentReactions, "comment_id", comment.id, user?.id);
    const commentAuthor = comment.profiles?.display_name ?? "Member";

    return (
      <div key={comment.id} className={`flex gap-2.5 ${nested ? "ml-8 border-l border-border/70 pl-3" : ""}`}>
        <button type="button" onClick={() => navigate(`/community/member/${comment.user_id}`)} className="shrink-0">
          <Avatar className={nested ? "h-7 w-7" : "h-8 w-8"}>
            {comment.profiles?.avatar_url ? <AvatarImage src={comment.profiles.avatar_url} /> : null}
            <AvatarFallback className="bg-primary/20 text-xs font-semibold text-primary">{commentAuthor[0]}</AvatarFallback>
          </Avatar>
        </button>
        <div className="min-w-0 flex-1">
          <div className="rounded-[14px] bg-muted/55 px-3 py-2.5">
            <div className="flex items-start gap-2">
              <button type="button" onClick={() => navigate(`/community/member/${comment.user_id}`)} className="min-w-0 flex-1 text-left">
                <AppText fontSize="$sm" fontWeight="700">{commentAuthor}</AppText>
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">{formatTime(comment.created_at)}</AppText>
              </button>
              {canRemoveComment ? (
                <MessageActions
                  menuTitle="Comment actions"
                  menuDescription="Choose what to do with this comment."
                  triggerLabel="More comment actions"
                  onEdit={canEditComment ? () => {
                    setEditingCommentId(comment.id);
                    setEditText(comment.content);
                  } : undefined}
                  onDelete={() => void deleteComment(comment.id, isAdminModeratingComment)}
                  deleteLabel={isAdminModeratingComment ? "Remove comment" : "Delete comment"}
                  showEdit={canEditComment}
                />
              ) : null}
            </div>

            {editingCommentId === comment.id ? (
              <div className="mt-2 space-y-2">
                <AppTextArea value={editText} onChangeText={setEditText} rows={2} />
                <div className="flex gap-2">
                  <AppButton type="button" size="sm" onPress={() => void saveEditComment(comment.id)}>Save</AppButton>
                  <AppButton type="button" tone="outline" size="sm" onPress={() => setEditingCommentId(null)}>Cancel</AppButton>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                <KindredSharedContent content={comment.content} textClassName="text-sm leading-6 text-foreground" />
                {comment.edited_at ? <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">Edited</AppText> : null}
              </div>
            )}
          </div>

          {editingCommentId !== comment.id ? (
            <div className="mt-1 flex flex-wrap items-center gap-3 px-2">
              <button type="button" onClick={() => startReply(comment, rootId)} className="text-xs font-semibold text-muted-foreground hover:text-primary">Reply</button>
              <ReactionBar reactions={reactions} onToggleReaction={(emoji) => toggleCommentReaction(comment.id, emoji)} />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <MemberPageScaffold title="Post" subtitle={locationLabel ? `Kinfeed · ${locationLabel}` : "Kinfeed conversation"} fallbackPath="/kinfeed">
      <AppCard>
        <AppCardBody gap="$md">
          <div className="flex items-start gap-3">
            <button type="button" onClick={() => navigate(`/community/member/${post.user_id}`)}>
              <Avatar className="h-10 w-10">
                {post.profiles?.avatar_url ? <AvatarImage src={post.profiles.avatar_url} /> : null}
                <AvatarFallback className="bg-primary/20 text-sm font-semibold text-primary">{authorName[0]}</AvatarFallback>
              </Avatar>
            </button>
            <div className="min-w-0 flex-1">
              <button type="button" onClick={() => navigate(`/community/member/${post.user_id}`)} className="text-left">
                <AppText fontSize="$sm" fontWeight="700">{authorName}</AppText>
              </button>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {formatTime(post.created_at)}{locationLabel ? ` · ${locationLabel}` : ""}
              </AppText>
            </div>
            <MessageActions
              menuTitle="Post actions"
              menuDescription="Choose what to do with this Kinfeed post."
              triggerLabel="More post actions"
              actions={[
                { id: "save", label: saved ? "Remove from saved" : "Save post", icon: <Bookmark size={16} />, onSelect: handleToggleSaved },
                { id: "hide", label: "Hide from my feed", icon: <EyeOff size={16} />, onSelect: async () => {
                  if (!user?.id) return;
                  const { error } = await hideFeedPost(user.id, post.id);
                  if (error) toast.error("Couldn't hide this post");
                  else { toast.success("Post hidden"); navigate("/kinfeed"); }
                } },
                ...(post.user_id !== user?.id ? [
                  { id: "mute", label: `Mute ${authorName}`, icon: <VolumeX size={16} />, onSelect: async () => {
                    if (!user?.id) return;
                    const { error } = await muteFeedMember(user.id, post.user_id);
                    if (error) toast.error("Couldn't mute this member");
                    else { toast.success(`${authorName} muted in Kinfeed`); navigate("/kinfeed"); }
                  } },
                  { id: "report", label: "Report post", icon: <Flag size={16} />, onSelect: () => setReportOpen(true) },
                ] : []),
                { id: "external-share", label: "Share outside Kindred", icon: <Share2 size={16} />, onSelect: shareOutsideKindred },
              ]}
              onEdit={canEditPost ? () => setEditingPost(true) : undefined}
              onDelete={canRemovePost ? () => void deletePost(isAdminModeratingPost) : undefined}
              deleteLabel={isAdminModeratingPost ? "Remove from Kinfeed" : "Delete post"}
              showEdit={canEditPost}
            />
          </div>

          {editingPost ? (
            <div className="space-y-3">
              <AppTextArea value={postEditText} onChangeText={setPostEditText} maxLength={KINFEED_POST_MAX_LENGTH} rows={4} />
              <div className="space-y-2">
                <AppText fontSize="$sm" fontWeight="700">Topics</AppText>
                <div className="flex flex-wrap gap-2">
                  {KINFEED_TOPIC_TAGS.map((tag) => {
                    const selected = postEditTopics.includes(tag.id);
                    return (
                      <button key={tag.id} type="button" onClick={() => togglePostEditTopic(tag.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${selected
                        ? "border-primary/20 bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/20 hover:text-primary"}`}>
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <AppButton type="button" size="sm" onPress={() => void savePostEdit()}>Save</AppButton>
                <AppButton type="button" tone="outline" size="sm" onPress={() => {
                  setEditingPost(false);
                  const parsed = parseKindredShareContent(post.content);
                  setPostEditText(parsed.payload ? parsed.text : post.content);
                  setPostEditTopics(normalizeKinfeedTopicTags(post.topic_tags));
                }}>Cancel</AppButton>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {postTopicTags.length ? (
                <div className="flex flex-wrap gap-2">
                  {postTopicTags.map((tagId) => (
                    <span key={tagId} className="rounded-full border border-primary/10 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary">
                      {getKinfeedTopicTag(tagId)?.label ?? tagId}
                    </span>
                  ))}
                </div>
              ) : null}
              <KindredSharedContent content={post.content} linkPreview={post.link_preview} textClassName="text-sm leading-relaxed text-foreground" />
              {post.edited_at ? <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">Edited</AppText> : null}
            </div>
          )}

          <KinfeedMedia photos={photos} videos={videos} onOpenPhoto={(index) => setFullScreenImages({ images: photos, index })} />

          {(Object.keys(postReactionSummary).length || comments.length) ? (
            <div className="flex min-h-8 items-center justify-between gap-3 border-b border-border/60 pb-2">
              <ReactionBar reactions={postReactionSummary} onToggleReaction={togglePostReaction} variant="summary" disabled />
              {comments.length ? <AppText color="$colorMuted" fontSize="$sm">{comments.length} comment{comments.length === 1 ? "" : "s"}</AppText> : null}
            </div>
          ) : null}

          <div className="grid grid-cols-3 items-stretch border-t border-border/60 pt-1">
            <ReactionBar reactions={postReactionSummary} onToggleReaction={togglePostReaction} onPrimaryAction={() => togglePostReaction("❤️")} variant="action" />
            <button type="button" onClick={() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-sm font-semibold text-muted-foreground hover:bg-muted/70 hover:text-primary">
              <MessageCircle size={17} /> Comment
            </button>
            <div className="flex min-w-0 items-stretch justify-center">
              <KindredShareSheet payload={sharePayload} triggerLabel="Share post" triggerTone="ghost" triggerContent={<><Share2 size={17} /><span>Share</span></>} />
            </div>
          </div>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader title="Comments" subtitle={`${comments.length} comment${comments.length === 1 ? "" : "s"}`} />
          {!commentThreads.length ? (
            <AppEmptyState title="No comments yet" body="Be the first person to comment." />
          ) : (
            <div className="space-y-4">
              {commentThreads.map(({ comment, replies }) => (
                <div key={comment.id} className="space-y-3">
                  {renderComment(comment, comment.id)}
                  {replies.map((reply) => renderComment(reply, comment.id, true))}
                </div>
              ))}
            </div>
          )}
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <div ref={composerRef}>
            <AppSectionHeader
              title={replyTarget ? `Reply to ${replyTarget.name}` : "Add a comment"}
              subtitle={replyTarget ? "Your reply will appear under this comment." : "Keep the conversation useful and kind."}
            />
          </div>
          {replyTarget ? (
            <AppButton type="button" tone="ghost" size="sm" alignSelf="flex-start" onPress={() => setReplyTarget(null)}>
              Cancel reply
            </AppButton>
          ) : null}
          <div className="flex flex-col gap-3 sm:flex-row">
            <MentionComposerField
              value={newComment}
              onChangeText={setNewComment}
              mentions={newCommentMentions}
              onMentionsChange={setNewCommentMentions}
              lookupCandidates={(query) => searchAllMentionSuggestions(query, user?.id)}
              placeholder={replyTarget ? `Reply to ${replyTarget.name}...` : "Write a comment..."}
              fieldProps={{ flex: 1 }}
              inputProps={{
                onKeyDown: (event: any) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendComment();
                  }
                },
                flex: 1,
              }}
            />
            <AppButton type="button" size="sm" onPress={() => void sendComment()} disabled={sending || !newComment.trim()}>
              <Send size={16} />
              {sending ? "Sending..." : "Send"}
            </AppButton>
          </div>
        </AppCardBody>
      </AppCard>

      {fullScreenImages ? (
        <FullScreenImage images={fullScreenImages.images} currentIndex={fullScreenImages.index} onClose={() => setFullScreenImages(null)} onNavigate={(index) => setFullScreenImages({ ...fullScreenImages, index })} />
      ) : null}
      <ReportSheet open={reportOpen} onOpenChange={setReportOpen} contentType="feed_post" contentId={postId} reportedUserId={post.user_id} />
    </MemberPageScaffold>
  );
};

export default FeedPostDetail;
